'use client';

import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Button, Typography } from '@mui/material';
import Slide from '@mui/material/Slide';
import Stack from '@mui/material/Stack';
import { isEmpty } from 'lodash';
import { useQueryState } from 'nuqs';
import React, { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSendParticipation } from 'src/api/connect';
import { useGetEvent } from 'src/api/events';
import { useGetWaitingListStatus } from 'src/api/hooks/waiting-list';
import { useAuthContext } from 'src/auth/hooks';
import Iconify from 'src/components/iconify';
import { useBoolean } from 'src/hooks/use-boolean';
import { useResponsive } from 'src/hooks/use-responsive';
import { useTranslate } from 'src/locales';
import { useParams, useRouter } from 'src/routes/hooks';

import EventPriceCard from '../../components/event-price-card/event-price-card';
import { CartItemDTO, CartShopItemDTO, EventType } from '../../types';
import { CreateProfileDialog } from './create-profile';
import EventPromoCode from './event-promocode';
import TicketItem from './event-ticket-item';
import { getButtonLabel } from './event-utils';
import { useTicketCalculations } from './useTicketCalculations';

interface IEventTicketListProps {
  eventId: number;
  isDark?: boolean;
}

const PulsatingDot = () => (
  <div
    style={{
      animation: 'pulseDot 1.25s cubic-bezier(0.455, 0.03, 0.515, 0.955) -0.4s infinite',
      backgroundColor: 'var(--pulsating-dot, #FFD369)',
      borderRadius: '50%',
      boxSizing: 'border-box',
      height: '12px',
      width: '12px',
      position: 'relative',
    }}
  >
    <style>
      {`
          @keyframes pulseRing {
            0% { transform: scale(.5); }
            80%, 100% { opacity: 0; }
          }

          @keyframes pulseDot {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.1); }
          }
        `}
    </style>
    <div
      style={{
        animation: 'pulseRing 1.25s cubic-bezier(0.215, 0.61, 0.355, 1) infinite',
        backgroundColor: 'var(--pulsating-dot, #FFD369)',
        borderRadius: '45px',
        content: '""',
        display: 'block',
        height: '300%',
        left: '-100%',
        position: 'absolute',
        top: '-100%',
        width: '300%',
      }}
    />
  </div>
);

export default function EventTicketList({ eventId, isDark }: IEventTicketListProps) {
  const { authenticated, user } = useAuthContext();
  const router = useRouter();
  const showCreateProfileDialog = useBoolean();
  const mdDown = useResponsive('down', 'md');
  const { id } = useParams<{ id: string }>();

  const { data: event } = useGetEvent(id, user);

  const { t } = useTranslate('common'); // Destructure the t function for translations
  const { t: tEvent } = useTranslate('event');
  const [___, setIsExclusiveEvent] = useQueryState('exclusive');
  const { data: alreadyInWaitingList } = useGetWaitingListStatus(eventId, true);

  const { t: tConnect } = useTranslate('connect'); // Destructure the t function for translations
  const [_, setCheckout] = useQueryState('checkout');
  const [__, setOpenLogin] = useQueryState('login');
  const [____, setWaitingList] = useQueryState('waiting-list');
  const [_____, setReturnTo] = useQueryState('returnTo');

  const { sendParticipation, loading } = useSendParticipation();
  const checkoutModal = useBoolean();

  const methods = useFormContext();

  const {
    watch,
    getValues,
    formState: { isSubmitting },
  } = methods;

  const tickets = watch('tickets') as CartItemDTO[];
  const shopItemsList = watch('shopItems') as CartShopItemDTO[];

  const {
    serviceFees,
    isFreeTicket,
    promoCode,
    promoCodeError,
    discountedTotalCost,
    appliedDiscount,
    hasTicketWithQuantityMoreThanZero,
    isUpdating,
    handleIncrease,
    handleDecrease,
  } = useTicketCalculations({
    tickets,
    shopItemsList,
    getValues,
    eventId,
  });

  // FIXME: hardcoded event id to be removed after waiting list feature is implemented
  const hasWaitingList = event?.configuration?.waitingListEnabled;

  console.log('hasWaitingList', hasWaitingList, event);

  const handleClickOnConnect = useCallback(() => {
    if (!authenticated) {
      setOpenLogin('open');
      setReturnTo(`https://trybe.fm/event/${id}/attendees`);
      return null;
    }
    if (!user?.profile?.description || !user?.pictures || !user?.profile?.interests) {
      showCreateProfileDialog.onTrue();
      return null;
    }

    if (event?.type === EventType.EXCLUSIVE && isEmpty(event?.tickets)) {
      setIsExclusiveEvent('open');
      return null;
    }
    sendParticipation(eventId?.toString(), { connect: true });

    router.push(`attendees`);

    return null;
  }, [
    authenticated,
    user,
    event,
    eventId,
    id,
    setReturnTo,
    setOpenLogin,
    sendParticipation,
    setIsExclusiveEvent,
    showCreateProfileDialog,
    router,
  ]);

  const onOpenWaitingList = () => {
    setWaitingList('true');
  };

  const handleCheckout = useCallback(() => {
    if (!authenticated) {
      setOpenLogin('open');
      return null;
    }
    setCheckout('true');
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated, checkoutModal, router]);

  return (
    <Stack gap={2}>
      <Typography variant="h5" color={isDark ? '#000' : '#fff'}>
        {tEvent('tickets')}
      </Typography>
      <Stack direction="column" width={mdDown ? '100%' : '80%'} gap={2}>
        {hasWaitingList && (
          <Stack
            gap={2}
            justifyContent="space-between"
            sx={{ backgroundColor: '#000', px: 2, py: 2, borderRadius: 2 }}
          >
            <Stack gap={1}>
              <Stack gap={1} direction="row" alignItems="center">
                <Iconify icon="guidance:waiting-room" width={18} />
                <Typography variant="h5" flexGrow={1}>
                  {alreadyInWaitingList
                    ? tEvent('waiting-list.already-in')
                    : tEvent('waiting-list.title')}
                </Typography>
              </Stack>
              <Typography variant="caption" fontSize={12} color="#b2afaf">
                {alreadyInWaitingList
                  ? tEvent('waiting-list.already-in-message')
                  : tEvent('waiting-list.message')}
              </Typography>
            </Stack>
            {!alreadyInWaitingList && (
              <Button
                variant="contained"
                color="primary"
                onClick={onOpenWaitingList}
                disabled={alreadyInWaitingList}
              >
                {tEvent('waiting-list.button')}
              </Button>
            )}

            {alreadyInWaitingList && <PulsatingDot />}
          </Stack>
        )}
        {event && event?.ticketSettings?.length > 0 && (
          <TicketItem
            handleIncrease={handleIncrease}
            handleDecrease={handleDecrease}
            currency={event?.currency}
          />
        )}

        {mdDown && (
          <Slide direction="up" in={hasTicketWithQuantityMoreThanZero} mountOnEnter unmountOnExit>
            <Stack
              sx={{
                position: 'fixed',
                zIndex: 100,
                bottom: 0,
                right: 0,
                width: '100vw',
                flexGrow: 1,
              }}
              gap={2}
            >
              <LoadingButton
                loading={loading}
                variant="contained"
                aria-label="scroll back to top"
                sx={{
                  transform: 'translate(60%, 0%)',
                  borderRadius: 1,
                  width: 'fit-content',
                  zIndex: 99,
                  p: 1.3,
                }}
                onClick={() => handleClickOnConnect()}
              >
                {tConnect('buttons.connect-with-people')}
                <Box
                  component="img"
                  alt="logo"
                  src="/assets/icons/connect/connect-icon.svg"
                  sx={{ width: 20, height: 20, ml: 1 }}
                />
              </LoadingButton>
              <Stack
                sx={{
                  backgroundColor: '#000',
                  paddingTop: 2,
                  paddingBottom: 4,
                  px: 3,
                  borderTopLeftRadius: 15,
                  borderTopRightRadius: 15,
                }}
              >
                {!isFreeTicket && (
                  <EventPromoCode
                    appliedPromoCode={promoCode}
                    error={promoCodeError}
                    isLoading={isUpdating}
                  />
                )}

                <EventPriceCard
                  discount={appliedDiscount}
                  appliedPromoCode={promoCode}
                  fees={serviceFees}
                  currency={event?.currency}
                  isFreeTicket={isFreeTicket}
                  total={discountedTotalCost}
                  isLoading={isUpdating}
                />
                {event?.type === EventType.EXCLUSIVE && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    gap={1}
                    sx={{ border: '1px solid primary', mb: 2, color: 'primary' }}
                  >
                    <Iconify icon="solar:info-square-outline" height={15} width={15} />
                    <Typography variant="caption">{tEvent('subject-to-approval')}</Typography>
                  </Stack>
                )}

                {isEmpty(shopItemsList) ? (
                  <LoadingButton
                    variant="contained"
                    type="submit"
                    aria-label="scroll back to top"
                    size="large"
                    disabled={event?.hasPendingRequest || isUpdating}
                    sx={{
                      borderRadius: 1,
                    }}
                    loading={isSubmitting || isUpdating}
                  >
                    {getButtonLabel({
                      hasPendingRequest: event?.hasPendingRequest,
                      type: event?.type ?? EventType.PUBLIC,
                      hasShopItem: !isEmpty(shopItemsList),
                      isNotCheckoutModal: true,
                      t,
                    })}
                  </LoadingButton>
                ) : (
                  <LoadingButton
                    variant="contained"
                    aria-label="scroll back to top"
                    size="large"
                    disabled={event?.hasPendingRequest || isUpdating}
                    loading={isSubmitting || isUpdating}
                    sx={{
                      borderRadius: 1,
                    }}
                    onClick={() => handleCheckout()}
                  >
                    {getButtonLabel({
                      hasPendingRequest: event?.hasPendingRequest,
                      type: event?.type ?? EventType.PUBLIC,
                      hasShopItem: !isEmpty(shopItemsList),
                      isNotCheckoutModal: true,
                      t,
                    })}
                  </LoadingButton>
                )}
              </Stack>
            </Stack>
          </Slide>
        )}
      </Stack>
      <CreateProfileDialog
        open={showCreateProfileDialog.value}
        onClose={() => showCreateProfileDialog.onFalse()}
      />
    </Stack>
  );
}
